/* eslint-disable max-statements */

import { Button, Flex, LoadingOverlay, Stack } from '@mantine/core';
import { useEffect, useState } from 'react';

import { deleteIcon } from '@assets';
import { Select, Table, TableColumnProps } from '@components';
import { useExternalRestrictedUsers } from '@hooks/customer';

interface ExternalRestrictedUsersProps {
  list: { value: string; label: string }[];
  onUpdate(ids: string[]): void;
}

export const ExternalRestrictedUsers: React.FC<ExternalRestrictedUsersProps> = ({ list = [], onUpdate }) => {
  const [newValue, setNewValue] = useState<string>(null);
  const [rows, setRows] = useState(list.slice());

  const { data: externalUsers, isLoading } = useExternalRestrictedUsers({ fullName: '' });

  const removeItem = (id: string) => {
    const newRows = rows.filter((r) => r.value !== id);
    setRows(newRows);
    onUpdate(newRows.map((r) => r.value));
  };

  useEffect(() => {
    setRows(list.slice());
  }, [list]);

  const columns: TableColumnProps<any>[] = [
    {
      name: 'User',
      cell: (row) => row.label,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <Button type="button" onClick={() => removeItem(row.value)} size="lg" variant="transparent">
          <img src={deleteIcon} alt={'delete'} />
        </Button>
      ),
    },
  ];

  const onChange = (event: any) => {
    setNewValue(event);
  };

  const addItem = () => {
    if (!newValue || rows.findIndex((r) => r.value === newValue) !== -1) {
      return;
    }

    const selectedUser = externalUsers.find((user) => user._id === newValue);
    const newRows = [...rows, { value: selectedUser._id, label: `${selectedUser.firstName} ${selectedUser.lastName}` }];

    onUpdate(newRows.map((r) => r.value));
    setRows(newRows);
    setNewValue(null);
  };

  if (!externalUsers) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Stack maw={300} mt="md">
      <Table columns={columns} dataSource={rows || []} pagination verticalSpacing="xs" />

      <Flex gap="md">
        <Select
          label="Insert an user.."
          onChange={onChange}
          data={externalUsers
            ?.filter((user) => !rows.find((userInList) => userInList.value === user._id))
            .map((usr) => ({
              value: usr._id,
              label: `${usr.firstName} ${usr.lastName}`,
            }))}
          value={newValue}
        />

        <Button type="button" onClick={addItem} disabled={!newValue} variant="filled" size="lg">
          +
        </Button>
      </Flex>
    </Stack>
  );
};
